import { useEffect, useRef } from 'react';
import {
  Map as GLMap,
  MapRef,
  MapEventType,
  MapEventOf,
} from 'shared/map-exports';

export type MapEventTarget = MapRef | GLMap;

/**
 * Registers map event listener(s) on mount and removes them on unmount.
 *
 * @example
 * useMapEvent('moveend', (event) => { ... }, map, true);              // single event
 * useMapEvent(['moveend', 'zoomend'], (event) => { ... }, map, true); // multiple events
 * useMapEvent('moveend', handler, map, visible);                      // off while hidden
 *
 * @param event - Event name, or array of names, to bind to.
 * @param handler - Listener; may be inline (does not need memoizing).
 *   Reading the firing map inside the handler:
 *   - Prefer `event.target`: it is the firing instance and non-null, so no
 *     undefined check is needed.
 *   - That non-null guarantee holds only synchronously. Do not assume it is
 *     alive across await / setTimeout; read what you need first.
 *   - It is the raw mapbox-gl `Map`, not a `MapRef`, so consumers should accept
 *     `MapRef | Map`.
 *   - If you already pass an explicit `map`, reading it directly is fine too.
 * @param map - The map instance to bind to. `null`/`undefined` are accepted
 *   and disable the subscription, so callers can pass a potentially-unready
 *   instance directly without guarding.
 * @param visible - Whether the calling layer is visible. When `false`,
 *   listeners are not registered (and any registered ones are removed), so
 *   hidden layers don't pay for map events. Pass the layer's `visible` prop,
 *   or `true` when there is no visibility concept at the call site.
 */
export const useMapEvent = <T extends MapEventType>(
  event: T | T[],
  handler: (event: MapEventOf<T>) => void,
  map: MapEventTarget | null | undefined,
  visible: boolean,
): void => {
  // Ref so inline handlers don't trigger re-subscription.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // Stable primitive key for the effect deps.
  const events = Array.isArray(event) ? event : [event];
  const eventKey = events.join(',');

  useEffect(() => {
    if (!map || !visible || !eventKey) {
      return undefined;
    }

    const listener = (e: MapEventOf<T>): void => {
      handlerRef.current(e);
    };

    events.forEach((eventName) => {
      map.on(eventName, listener);
    });

    return () => {
      events.forEach((eventName) => {
        map.off(eventName, listener);
      });
    };
    // events is always in sync with eventKey (same render, same prop), so
    // omitting it from deps is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, visible, eventKey]);
};
